<script lang="ts">
export default {
  name: 'AccountProgramList',
};

interface ProgramYearGroup {
  year: number;
  programs: Program[];
}
</script>

<script setup lang="ts">
import { ref, watch, computed, onMounted, inject } from 'vue';
import { CircleUser, Plus, Pencil, EllipsisVertical, Files, Copy, Trash2, LayoutList, Calendar } from '@lucide/vue';
import { accountService } from '@/services/api';
import { Program } from '@/types/Programs';
import FilterBar from '@/components/v2/FilterBar.vue';
import { compactMoney } from '@/utilities/Money';
import ConfirmModal from '@/components/ConfirmModal.vue';
import { IsUserAClient } from '@/utilities/AuthUtils';
import { AppState } from '@/store';
import { filters } from '@/utilities/Filters';
import { Store } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import Skeleton from '@/components/v2/Skeleton.vue';
import MenuDropdown, { type MenuItem } from '@/components/v2/MenuDropdown.vue';
import AppButton from '@/components/v2/AppButton.vue';
import Chip from '@/components/v2/Chip.vue';
import Toggle from '@/components/v2/Toggle.vue';
import EmptyDisplay from '@/components/EmptyDisplay.vue';
import emptySrc from '@/assets/images/empty.svg';

const $router = useRouter();
const $route = useRoute();

// -----
// data
// -----
const loading = ref(false);
const programs = ref<Program[]>([]);
const accountId = ref('');
const showCurrentOnly = ref(true);
const programsToMerge = ref<number[]>([]);
const deleteProgramId = ref<number | null>(null);
const showDeleteModal = ref(false);

const store = inject<Store<AppState>>('store');

// ---------
// watchers
// ---------
watch(showCurrentOnly, async () => {
  await loadPrograms();
});

// ---------
// computed
// ---------
const hasPrograms = computed(() => {
  return programs.value && programs.value.length > 0;
});

const yearGroupedPrograms = computed(() => {
  if (!hasPrograms.value) {
    return [];
  }
  return Object.values(
    programs.value.reduce(
      (acc, curr) => {
        if (!acc[curr.year]) {
          acc[curr.year] = { year: curr.year, programs: [] };
        }
        acc[curr.year].programs.push(curr);
        return acc;
      },
      {} as Record<string, ProgramYearGroup>,
    ),
  ).sort((a, b) => Number(b.year) - Number(a.year));
});

const isAClientAccount = computed(() => {
  return IsUserAClient(store?.state.user);
});

const sortedPrograms = computed(() => {
  const sortedPrograms = [...programs.value];
  return sortedPrograms.sort((a, b) => {
    if (a.year === b.year) {
      return a.name.localeCompare(b.name);
    }
    return b.year - a.year;
  });
});

// -----------
// lifecycles
// -----------
onMounted(async () => {
  accountId.value = $route.params.id as string;
  await loadPrograms();

  // if no current programs, then load expired
  if (programs.value.length === 0) {
    showCurrentOnly.value = false;
  }
});

// ----------
// functions
// ----------
function isProgramAppGenerated(program: Program): boolean {
  return program.type === 'Automated' || program.type === 'EPIC';
}

function toggleMergedProgram(programId: number) {
  if (programsToMerge.value.includes(programId)) {
    programsToMerge.value = programsToMerge.value.filter((id) => id !== programId);
  } else {
    programsToMerge.value.push(programId);
  }
}

async function mergePrograms(): Promise<boolean> {
  await $router.push(`/account/${accountId.value}/programs/new?mergedPrograms=${programsToMerge.value.join(',')}`);
  return true;
}

async function deleteProgram(programID: number): Promise<boolean> {
  await accountService.deleteProgram(accountId.value, programID);
  await loadPrograms();
  return true;
}

async function loadPrograms(): Promise<void> {
  loading.value = true;
  programs.value = await accountService.getProgramsForAccount(accountId.value, !showCurrentOnly.value, false, true);
  loading.value = false;
}

function limitValue(program: Program): string | number {
  if (program.coverageTypes.length === 1 && program.coverageTypes[0] === 'Work Comp') {
    return 'Statutory';
  }
  return compactMoney(program.maxLimitInclAttachment) ?? '--';
}

function getProgramMenuItems(program: Program): MenuItem[] {
  const items: MenuItem[] = [];
  items.push({
    label: 'Open',
    handler: () => $router.push(`/account/${accountId.value}/programs/${program.id}`),
    icon: Files,
  });
  if (!isProgramAppGenerated(program) && !isAClientAccount.value) {
    items.push({
      label: 'Edit',
      handler: () => $router.push(`/account/${accountId.value}/programs/${program.id}/edit`),
      icon: Pencil,
    });
  }
  if (!isAClientAccount.value) {
    items.push({
      label: 'Copy',
      handler: () => $router.push(`/account/${accountId.value}/programs/new?copiedProgramID=${program.id}`),
      icon: Copy,
    });
    items.push({
      label: 'Delete',
      handler: () => {
        deleteProgramId.value = program.id;
        showDeleteModal.value = true;
      },
      icon: Trash2,
      iconColorClass: 'text-error',
      textColorClass: 'text-error',
    });
  }
  return items;
}

async function handleDeleteProgram(): Promise<boolean> {
  if (deleteProgramId.value !== null) {
    await deleteProgram(deleteProgramId.value);
    deleteProgramId.value = null;
    showDeleteModal.value = false;
  }
  return true;
}
</script>

<template>
  <div>
    <FilterBar
      class="filter-bar"
      v-if="!loading">
      <div class="flex w-full justify-between">
        <div class="flex">
          <RouterLink
            v-if="!isAClientAccount"
            :to="`/account/${accountId}/programs/new`"
            custom
            v-slot="{ navigate }">
            <AppButton
              primary
              data-testid="create-program"
              @click="navigate">
              <Plus :size="16" />
              Create New Program
            </AppButton>
          </RouterLink>

          <ConfirmModal
            v-if="hasPrograms"
            title="Select Programs to Merge"
            :action="mergePrograms"
            :dismiss="() => (programsToMerge = [])"
            confirmText="Merge"
            size="md"
            :is-confirm-disabled="() => programsToMerge.length < 2">
            <template v-slot:button="{ props }">
              <slot
                name="button"
                :props="props">
                <AppButton
                  v-if="!isAClientAccount"
                  primary
                  class="ml-4 justify-start rounded-full"
                  data-testid="merge-programs"
                  v-bind="props">
                  <Plus :size="16" />
                  Create From Existing Programs
                </AppButton>
              </slot>
            </template>
            <div class="flex flex-row">
              <ul class="w-full list-none space-y-2 pl-4">
                <li
                  v-for="program in sortedPrograms"
                  :key="program.id"
                  class="flex grow cursor-pointer flex-row items-center rounded p-1 text-sm text-black/70 hover:bg-gray-100"
                  @click="toggleMergedProgram(program.id)"
                  :data-testid="`merge-select-${program.id}`">
                  <input
                    type="checkbox"
                    class="scale-125 cursor-pointer"
                    :checked="programsToMerge.includes(program.id)" />
                  <span class="ml-4 text-base">{{ program.name }}</span>
                </li>
              </ul>
            </div>
          </ConfirmModal>
        </div>
        <Toggle
          id="show-current"
          v-model="showCurrentOnly"
          label="Show Current Only" />
      </div>
    </FilterBar>

    <div class="m-4">
      <h1 class="my-2">Account Program Details</h1>

      <div
        class="grid grid-cols-4 gap-8 p-4 pb-4 max-[1150px]:grid-cols-3 max-[900px]:grid-cols-2 max-[600px]:grid-cols-1"
        v-if="loading">
        <Skeleton
          v-for="i in 4"
          :key="i"
          shape="rectangle"
          width="100%"
          height="120px"
          class="rounded-md" />
      </div>

      <div
        class="grid grid-cols-1 p-4"
        v-else-if="!loading && hasPrograms">
        <div
          class="mt-8 mb-4 first:mt-0"
          v-for="(programGroup, index) in yearGroupedPrograms"
          :key="index">
          <h2 class="mb-4 border-0 border-b border-solid border-gray-200 text-2xl">
            {{ programGroup.year }}
          </h2>

          <div class="grid grid-cols-4 gap-8 pb-4 max-[1150px]:grid-cols-3 max-[900px]:grid-cols-2 max-[600px]:grid-cols-1">
            <div
              v-for="(program, pIndex) in programGroup.programs"
              :key="pIndex"
              class="grid grid-rows-[auto_auto_auto] rounded border border-solid border-gray-400">
              <div class="grid grid-cols-[1fr_auto] gap-x-2 px-4 pt-4">
                <div class="grid grid-cols-[auto_auto_1fr] items-baseline gap-x-1">
                  <div class="text-[1.2rem] leading-[1.1] font-bold">{{ program.name }}</div>
                </div>
                <div class="grid grid-cols-[1fr_auto] items-center gap-x-1">
                  <RouterLink
                    class="btn btn-outline btn-sm btn-secondary"
                    :to="`/account/${accountId}/programs/${program.id}`"
                    :data-testid="`open-program-${program.id}`">
                    Open
                  </RouterLink>
                  <MenuDropdown
                    :items="getProgramMenuItems(program)"
                    left>
                    <template #action>
                      <label
                        tabindex="0"
                        class="btn btn-square btn-ghost btn-sm"
                        :data-testid="`${program.id}-actions-menu`">
                        <EllipsisVertical :size="20" />
                      </label>
                    </template>
                  </MenuDropdown>
                </div>
              </div>
              <div class="relative flex flex-row flex-nowrap items-center justify-between gap-x-4 p-4 pb-6">
                <div
                  v-if="!isAClientAccount"
                  class="flex min-w-0 shrink flex-col gap-1">
                  <div class="grid grid-cols-[auto_1fr] items-center gap-x-1">
                    <LayoutList
                      :size="16"
                      class="text-gray-500" />
                    <span
                      class="text-sm text-black/70"
                      :data-testid="`${program.id}-program-type`"
                      >{{ program.type }}</span
                    >
                  </div>
                  <div
                    class="grid grid-cols-[auto_1fr] items-center gap-x-1"
                    v-if="!isProgramAppGenerated(program)">
                    <CircleUser
                      :size="16"
                      class="text-gray-500" />
                    <span
                      class="text-info-darken-3 text-sm text-black/70"
                      :data-testid="`${program.id}-inserted-by`"
                      >{{ program.createdBy }}</span
                    >
                  </div>
                  <div
                    class="grid grid-cols-[auto_1fr] items-center gap-x-1"
                    v-if="!isProgramAppGenerated(program)">
                    <Calendar
                      :size="16"
                      class="text-gray-500" />
                    <span
                      class="text-sm text-black/70"
                      :data-testid="`${program.id}-last-updated`"
                      >Updated on {{ filters.date(program.lastUpdatedTime) }}</span
                    >
                  </div>
                </div>
                <div class="flex max-w-[50%] shrink-0 flex-wrap items-center justify-end gap-1">
                  <Chip
                    v-for="coverage in program.coverageTypes"
                    :key="coverage"
                    color-class="bg-[#4E738A] text-white"
                    size="sm">
                    {{ coverage }}
                  </Chip>
                </div>
              </div>
              <div class="flex justify-between gap-x-4 rounded-b border-0 border-t border-solid border-gray-200 bg-gray-100 px-4 py-2">
                <div class="flex flex-col items-center text-center">
                  <div class="text-xs uppercase">Policies</div>
                  <div class="text-base font-bold">{{ program.policyCount }}</div>
                </div>
                <div class="flex flex-col items-center text-center">
                  <div class="text-xs uppercase">Limit</div>
                  <div class="text-base font-bold">{{ limitValue(program) }}</div>
                </div>
                <div class="flex flex-col items-center text-center">
                  <div class="text-xs uppercase">Premium</div>
                  <div class="text-base font-bold">{{ compactMoney(program.totalPremiumSum) ?? '--' }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        <EmptyDisplay
          :src="emptySrc"
          alt="No programs found"
          title="No programs found"
          v-if="!hasPrograms"
          text="No programs are currently available for this client." />
      </div>
    </div>

    <ConfirmModal
      :show="showDeleteModal"
      title="Delete Program"
      :action="handleDeleteProgram"
      confirmText="Delete"
      size="lg"
      :dismiss="
        () => {
          showDeleteModal = false;
          deleteProgramId = null;
        }
      ">
      <span>Are you sure you want to delete this program?</span>
    </ConfirmModal>
  </div>
</template>
